<template>
  <dl class="field-set type-text instructions">
    <dt class="field-label">
      <svg-icon name="Info16" kind="indicative" />
      <span>{{ feedbackAssistantRequestLabel }}</span>
    </dt>
    <dd v-if="isMultipleLFA && isSAAndGC" class="field-value">
      <i18n-t keypath="lfa_sa_gc_question_banner_instructions_content" tag="span">
        <template #multipleLfaFeedbackMessage>
          {{ multipleLFAMessage }}
        </template>
        <template #saAppName>
          {{ saAppFeatureName }}
        </template>
        <template #afaAppLink>
          <cv-link :href="afaAppGuideUrl" target="_blank">{{
            t("lfa_gc_question_banner_instructions_link_content")
          }}</cv-link>
        </template>
      </i18n-t>
    </dd>
    <dd v-else-if="!isMultipleLFA && isGC" class="field-value">
      <i18n-t keypath="lfa_gc_question_banner_instructions_content" tag="span">
        <template #lfaAppName>
          {{ appFeatureName }}
        </template>
        <template #afaAppLink>
          <cv-link :href="afaAppGuideUrl" target="_blank">{{
            t("lfa_gc_question_banner_instructions_link_content")
          }}</cv-link>
        </template>
      </i18n-t>
    </dd>
    <dd v-else class="field-value">{{ feedbackAssistantRequestMessage }}</dd>
  </dl>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from "vue";
import { useStore } from "vuex";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";
import { isEmpty } from "@utils/var.check";
import LearningFeedbackMixin from "@modules/editor/mixin/learning.feedback.mixin";
import appConfigs from "@configs/app";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm.ts";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";

// 3. Props declaration
interface AppItem {
  appFeatureName: string;
  appFeatureId: number;
  category: string;
}
interface Props {
  appList: AppItem[];
  isInTeacherMarkedQuiz?: boolean;
  optionSelected?: never[];
}
const props = defineProps<Props>();

// 4. Emits (none in original, so skip)

// 6. i18n
const { t } = useI18n();
defineOptions({
  mixins: [LearningFeedbackMixin],
});
const { vm, vmExpose } = useVm();

const appType = ref<string>("");
const appFeatureId = ref<number | string | null>(null);
const appFeatureName = ref<string>("");

const store = useStore();
const getIntegratorAppAccessByAppFeatureId = (id: number | string | null) =>
  store.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByAppFeatureId`](id);

const isMultipleLFA = computed(() => !isEmpty(props.appList) && props.appList.length > 1);

const isLFA = computed(
  () =>
    appType.value === INTEGRATOR_APP_FEATURE_CATEGORY.AFA ||
    appType.value === INTEGRATOR_APP_FEATURE_CATEGORY.GC ||
    appType.value === INTEGRATOR_APP_FEATURE_CATEGORY.SA,
);

const isSE = computed(() => appType.value === INTEGRATOR_APP_FEATURE_CATEGORY.SE);
const isAK = computed(() => appType.value === INTEGRATOR_APP_FEATURE_CATEGORY.AK);
const isGC = computed(() => appType.value === INTEGRATOR_APP_FEATURE_CATEGORY.GC);
const isSA = computed(() => appType.value === INTEGRATOR_APP_FEATURE_CATEGORY.SA);

const computedAppFeatureName = computed(() => {
  const appFeature = getIntegratorAppAccessByAppFeatureId(appFeatureId.value);
  if (isEmpty(appFeature)) return "";
  return appFeature.appFeatureName;
});

const saAppFeatureName = computed(() => {
  if (!isEmpty(props.appList)) {
    const saApp = props.appList.filter(
      (app) => app.category === INTEGRATOR_APP_FEATURE_CATEGORY.SA,
    );
    return !isEmpty(saApp) ? saApp[0].appFeatureName : "";
  }
  return "";
});

const isSAAndGC = computed(() => {
  if (!isEmpty(props.appList)) {
    let isGC = false;
    let isSA = false;
    props.appList.forEach((app) => {
      isGC = isGC || app.category === INTEGRATOR_APP_FEATURE_CATEGORY.GC;
      isSA = isSA || app.category === INTEGRATOR_APP_FEATURE_CATEGORY.SA;
    });
    return isGC && isSA;
  }
  return false;
});

// information message
const feedbackAssistantRequestLabel = computed(() =>
  t("lfa_question_component_label_instructions"),
);

const multipleLFAMessage = computed(() => vm.getMultipleLFAMessage());
const lfaMessage = computed(() => vm.getLFAMessage(computedAppFeatureName.value));
const seMessage = computed(() => vm.getSEMessage(computedAppFeatureName.value));
const akMessage = computed(() => vm.getAKMessage(computedAppFeatureName.value));

const feedbackAssistantRequestMessage = computed(() => {
  if (isMultipleLFA.value) return multipleLFAMessage.value;
  if (isLFA.value) return lfaMessage.value;
  if (isSE.value) return seMessage.value;
  if (isAK.value) return akMessage.value;
  return "";
});

const afaAppGuideUrl = computed(() => appConfigs.annotatedFeedbackAssistantGuideUrl);

// 7. methods
function populateSingleAppMetadata() {
  if (!isEmpty(props.appList)) {
    const app = props.appList[0];
    appFeatureName.value = app.appFeatureName;
    appFeatureId.value = app.appFeatureId;
    appType.value = app.category;
  }
}

// 8. lifecycle
onBeforeMount(() => {
  populateSingleAppMetadata();
});

vmExpose({
  isSA,
});
</script>
